import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatusPill from "../components/StatusPill";
import { ArrowUpRight, PackagePlus } from "lucide-react";

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) {
    return (
      <div className="p-10 text-sm text-zinc-500 tracking-widest uppercase">
        Loading…
      </div>
    );
  }

  const kpis = [
    { label: "Pallets in stock", value: stats.in_stock, key: "in_stock" },
    { label: "Expiring ≤ 7 days", value: stats.expiring_soon, key: "expiring" },
    { label: "Expired", value: stats.expired, key: "expired" },
    { label: "Shipped today", value: stats.shipped_today, key: "shipped" },
  ];

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        eyebrow="Operations Overview"
        title="Dashboard."
        action={
          <Link
            to="/production"
            className="mj-btn mj-btn-primary"
            data-testid="quick-log-btn"
          >
            <PackagePlus size={16} /> Log Production
          </Link>
        }
      />

      <div className="p-6 md:p-10 space-y-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-grid">
          {kpis.map((k) => (
            <div key={k.key} className="mj-kpi" data-testid={`kpi-${k.key}`}>
              <div className="data-label">{k.label}</div>
              <div className="mj-kpi-num">{k.value}</div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="mj-card lg:col-span-2" data-testid="recent-pallets-card">
            <div className="flex items-end justify-between mb-4">
              <div>
                <div className="data-label mb-1">Recent activity</div>
                <h2 className="font-display text-2xl font-bold">
                  Latest Pallets Logged
                </h2>
              </div>
              <Link
                to="/inventory"
                className="text-xs tracking-widest uppercase font-bold flex items-center gap-1 hover:text-[var(--mj-red)]"
                data-testid="view-all-inventory"
              >
                View all <ArrowUpRight size={14} />
              </Link>
            </div>
            <table className="mj-table">
              <thead>
                <tr>
                  <th>Pallet #</th>
                  <th>Product</th>
                  <th>Expiry</th>
                  <th>Customer</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_pallets.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-zinc-500 py-8">
                      No pallets logged yet — start by{" "}
                      <Link to="/production" className="underline">
                        logging your first pallet
                      </Link>
                      .
                    </td>
                  </tr>
                )}
                {stats.recent_pallets.map((p) => (
                  <tr key={p.id}>
                    <td className="font-mono-id font-semibold">
                      <Link
                        to={`/pallets/${p.id}`}
                        className="hover:text-[var(--mj-red)]"
                        data-testid={`recent-pallet-link-${p.id}`}
                      >
                        {p.pallet_number}
                      </Link>
                    </td>
                    <td>
                      <div className="font-semibold">{p.product_name}</div>
                      <div className="text-xs text-[var(--mj-ink-muted)] font-mono-id">
                        {p.product_sku} · Lot {p.lot_number}
                      </div>
                    </td>
                    <td className="font-mono-id">{p.expiry_date}</td>
                    <td>{p.customer_name || "—"}</td>
                    <td>
                      <StatusPill status={p.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mj-card" data-testid="ops-summary-card">
            <div className="data-label mb-1">At a glance</div>
            <h2 className="font-display text-2xl font-bold mb-6">Ops Summary</h2>
            <ul className="space-y-4 text-sm">
              <li className="flex items-center justify-between">
                <span className="text-[var(--mj-ink-muted)]">Scheduled shipments</span>
                <span className="font-mono-id text-xl font-bold">
                  {stats.shipments_scheduled}
                </span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-[var(--mj-ink-muted)]">Products</span>
                <span className="font-mono-id text-xl font-bold">
                  {stats.products_count}
                </span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-[var(--mj-ink-muted)]">Customers</span>
                <span className="font-mono-id text-xl font-bold">
                  {stats.customers_count}
                </span>
              </li>
            </ul>
            <div className="mt-8 pt-6 border-t border-[var(--mj-border)]">
              <div className="data-label mb-3">Shortcuts</div>
              <div className="flex flex-wrap gap-2">
                <Link to="/inventory" className="mj-btn mj-btn-secondary" data-testid="shortcut-inventory">
                  Inventory
                </Link>
                <Link to="/shipments" className="mj-btn mj-btn-secondary" data-testid="shortcut-shipments">
                  Shipments
                </Link>
                <Link to="/reports" className="mj-btn mj-btn-secondary" data-testid="shortcut-reports">
                  Reports
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
